// =============================================================================
// SIREPPAR - Qué dimensión de verificación corresponde mirar
// =============================================================================
// Cada documento tiene DOS dimensiones independientes (admisibilidad y análisis)
// y la UI proyecta una sobre `estadoRevision`/`observacion`/`revisado*`. La
// pregunta "¿cuál de las dos?" se contestaba por separado en /documentos y en la
// tab Documentación del expediente, con reglas distintas: /documentos usaba la
// ÚLTIMA etapa alcanzada y el expediente sólo miraba si estaba EN_ANALISIS u
// OBSERVADO. Resultado: un reempadronamiento ya elevado al Secretario mostraba
// en el expediente el estado de admisibilidad (viejo) y en /documentos el de
// análisis — dos verdades para el mismo documento.
//
// La regla correcta es la de /documentos y vive acá: una vez que el trámite pasó
// la admisibilidad, lo que vale es análisis, incluso cuando el expediente ya se
// fue de la mesa de la supervisora (EN_SECRETARIO, EN_TEP, VIGENTE…).
// =============================================================================

export type EtapaVerificacion = 'ADMISIBILIDAD' | 'ANALISIS' | 'POSTERIOR' | null;

// En RECONOCIMIENTO la etapa se deriva de la FASE. Sólo la etapa de atributos es
// admisibilidad: de ATRIBUTOS_APROBADOS en adelante el trámite ya pasó a
// documentación (análisis) y no vuelve.
export const FASES_ANALISIS_RECON = ['DOCUMENTACION_PENDIENTE', 'DOCUMENTACION_EN_SECRETARIO'];

// Ojo: la lista de fases "posteriores" tiene que incluir TODO lo que viene
// después del provisorio (afiliados, autoridades, definitivo). Cuando sólo
// nombraba las DOCUMENTACION_*, un expediente en la etapa de afiliados caía en el
// `else` y el sistema lo trataba como si todavía estuviera en atributos: la
// pestaña de análisis quedaba deshabilitada.
export const FASES_POSTERIORES_RECON = [
  'DOCUMENTACION_EN_TEP', 'DOCUMENTACION_RECHAZADA', 'DOCUMENTACION_APROBADA',
  'AFILIADOS_PENDIENTES', 'AUTORIDADES_PENDIENTES',
  'DEFINITIVO_EN_TEP', 'DEFINITIVO_RECHAZADO', 'RECONOCIMIENTO_DEFINITIVO',
  // Legacy
  'RECONOCIMIENTO_PROVISORIO', 'CARGA_AFILIADOS_EN_CURSO', 'ELEVADO_TRIBUNAL',
];

// POSTERIOR = el expediente ya salió de la mesa de la supervisora. Incluye los
// estados finales del partido (VIGENTE / CADUCO / EXTINGUIDO / NO_PRESENTADO),
// que antes caían en `null` y por eso la vista se abría de nuevo en Admisibilidad.
const ESTADOS_POSTERIORES = [
  'EN_SECRETARIO', 'PENDIENTE_RESOLUCION', 'EN_TEP', 'NO_PRESENTADO',
  'APROBADO', 'RECHAZADO', 'ARCHIVADO',
  'VIGENTE', 'CADUCO', 'EXTINGUIDO',
];

/**
 * Etapa que el expediente YA ALCANZÓ (no la que se está mirando).
 * `null` = todavía no llegó a ninguna (BORRADOR / PRESENTADO), donde análisis no
 * tiene nada que mostrar.
 */
export function etapaAlcanzada(params: {
  tipo?: string | null;
  estado?: string | null;
  fase?: string | null;
}): EtapaVerificacion {
  const { tipo, estado, fase } = params;

  if (tipo === 'RECONOCIMIENTO') {
    if (fase && FASES_POSTERIORES_RECON.includes(fase)) return 'POSTERIOR';
    if (fase && FASES_ANALISIS_RECON.includes(fase)) return 'ANALISIS';
    return 'ADMISIBILIDAD';
  }

  if (estado === 'EN_ADMISIBILIDAD' || estado === 'INTIMADO') return 'ADMISIBILIDAD';
  if (estado === 'EN_ANALISIS' || estado === 'OBSERVADO') return 'ANALISIS';
  if (estado && ESTADOS_POSTERIORES.includes(estado)) return 'POSTERIOR';
  return null;
}

/**
 * Dimensión a proyectar por defecto sobre los documentos.
 *
 * En RECONOCIMIENTO la verificación es ÚNICA (una sola revisión por documento):
 * se usa siempre la dimensión 'ADMISIBILIDAD', así lo verificado en atributos
 * sigue verificado en documentación (análisis sólo agrega documentos nuevos, no
 * re-verifica lo anterior).
 */
export function dimensionPorDefecto(params: {
  tipo?: string | null;
  estado?: string | null;
  fase?: string | null;
}): 'ADMISIBILIDAD' | 'ANALISIS' {
  if (params.tipo === 'RECONOCIMIENTO') return 'ADMISIBILIDAD';
  const etapa = etapaAlcanzada(params);
  return etapa === 'ANALISIS' || etapa === 'POSTERIOR' ? 'ANALISIS' : 'ADMISIBILIDAD';
}


/**
 * ¿Este documento pesa en la dimensión que se está mirando?
 *
 * UN SOLO PREDICADO PARA OCULTAR Y PARA CONTAR. Es la parte importante.
 *
 * En ANÁLISIS no pesa el archivo que ya fue RECHAZADO en admisibilidad y no fue
 * rechazado en análisis: es el original que el partido reemplazó, historial de la
 * etapa anterior. La excepción de la segunda condición es real: un documento
 * rechazado EN análisis arrastra admisibilidad a RECHAZADO por cascada (ver
 * documentoService.updateEstadoRevision), y ése sí es de esta etapa.
 *
 * En ADMISIBILIDAD no se oculta nada: es la etapa donde ese rechazo se decidió.
 *
 * POR QUÉ ACÁ Y NO EN CADA PANTALLA: esta regla estaba escrita sólo en el filtro de
 * /documentos. El panel de secciones del expediente no la tenía y el backend
 * (`exigirRevisados`) tampoco, así que un archivo rechazado en admisibilidad se
 * ocultaba en /documentos pero seguía contando como pendiente en las otras dos: la
 * sección no se podía aprobar —"quedan 1 documentos pendientes"— por un archivo que
 * en la pantalla de revisión no aparecía. Sección trabada sin salida, 61 de ellas en
 * producción.
 *
 * La invariante que evita que vuelva a pasar: LO QUE SE OCULTA NO SE CUENTA. Las tres
 * superficies preguntan lo mismo, y el espejo en el backend está en
 * `tiposDocumentoExpedienteService.contarSinRevisar`. Si esta regla cambia, cambia
 * en los dos lados o vuelve el estado sin salida.
 */
export function pesaEnDimension(
  doc: {
    estadoRevisionAdmisibilidad?: string | null;
    estadoRevisionAnalisis?: string | null;
  },
  dimension: 'ADMISIBILIDAD' | 'ANALISIS',
): boolean {
  if (dimension === 'ADMISIBILIDAD') return true;
  return !(doc.estadoRevisionAdmisibilidad === 'RECHAZADO'
    && doc.estadoRevisionAnalisis !== 'RECHAZADO');
}
